import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Trans } from "react-i18next";import { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Download, CheckCircle2, Loader2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { markAnnouncementOpened, saveAnnouncementDocument } from "../../../services/announcementServices";
import { ROUTES, IMPORTANT_COLOR, GENERAL_COLOR } from "../constants/announcementOptions";

export function AnnouncementDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState(null);

  // Build the item from navigation state — the list page passes the full
  // card data so we never need to call any API just to display the detail.
  const raw = location.state?.item ?? null;
  const item = raw ? (() => {
    const cat = (raw.status ?? "").toLowerCase();
    const isImportant = cat === "important";
    return {
      announcementId: raw.announcementId ?? raw.id ?? id,
      title: raw.title ?? "",
      category: isImportant ? "Important" : "General",
      categoryColor: isImportant ? IMPORTANT_COLOR : GENERAL_COLOR,
      createdDate: raw.date ?? "",
      publishDate: raw.date ?? "",
      archiveDate: "",
      recipients: raw.recipients ?? "",
      createdBy: raw.createdBy ?? "",
      content: raw.content ?? "",
      hasSupportingDocument: raw.hasSupportingDocument ?? false,
    };
  })() : null;

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    setAckError(null);
    try {
      await markAnnouncementOpened(item?.announcementId ?? id);
      setAcknowledged(true);
    } catch (err) {
      setAckError(`Failed to acknowledge: ${err.message}`);
    } finally {
      setAcknowledging(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await saveAnnouncementDocument(item.announcementId);
    } catch (err) {
      setDownloadError(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (!item) {
    return (
      <AppLayout>
        <p className="p-8 text-sm text-error">Announcement not found. Please go back and try again.</p>
      </AppLayout>);
  }

  const Field = ({ label, value, style }) =>
  <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider per-file-hint mb-1">{label}</p>
      <p className="text-sm text-foreground" style={style}>{value || "—"}</p>
    </div>;


  return (
    <AppLayout>

      <div className="max-w-[800px] bg-white rounded-2xl p-7 sm:p-8 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60">
        <PageTitleRow back={<PageBackLink to={ROUTES.essList} />}>
          <h2 className="text-2xl font-bold text-foreground mb-6 wrap-break-word">{item.title}</h2>
        </PageTitleRow>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 border-y border-(--color-text-border)/70 py-5 mb-6">
          <Field label="Created On" value={item.createdDate} />
          <Field label="Publish Date" value={item.publishDate || item.createdDate} />
          <Field label="Archive Date" value={item.archiveDate} />
          <Field label="Category" value={item.category} style={{ color: item.categoryColor, fontWeight: 600 }} />
          {item.recipients && <Field label="Recipients" value={item.recipients} />}
          {item.createdBy && <Field label="Created By" value={item.createdBy} />}
        </div>

        <p className="text-sm leading-relaxed text-foreground whitespace-pre-line wrap-break-word">{item.content}</p>

        {item.hasSupportingDocument &&
        <div className="border-t border-(--color-text-border)/70 mt-6 pt-5">
            <p className="text-[12px] font-semibold uppercase tracking-wider per-file-hint mb-2.5"><Trans i18nKey="common.attachments" /></p>
            <button type="button" onClick={handleDownload} disabled={downloading}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-(--color-text-border) bg-workspace/40 text-sm text-foreground hover:bg-workspace disabled:opacity-60 disabled:cursor-not-allowed">
              <Download className="h-4 w-4" />
              {downloading ? "Downloading…" : "Download Document"}
            </button>
            {downloadError && <p className="mt-1.5 text-[13px] text-error">{downloadError}</p>}
          </div>
        }

        {/* Acknowledge button — always shown, becomes a success state once clicked */}
        <div className="border-t border-(--color-text-border)/70 mt-6 pt-5 flex flex-col items-start gap-2">
          {acknowledged ? (
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-(--color-success,#16a34a)/10 text-(--color-success,#16a34a) text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4" />
              Marked as Read &amp; Acknowledged
            </span>
          ) : (
            <button
              type="button"
              onClick={handleAcknowledge}
              disabled={acknowledging}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {acknowledging
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Acknowledging…</>
                : <><CheckCircle2 className="h-4 w-4" /> Mark as Read &amp; Acknowledged</>}
            </button>
          )}
          {ackError && <p className="text-[13px] text-error">{ackError}</p>}
        </div>
      </div>
    </AppLayout>);

}
